import SendIcon from '@mui/icons-material/Send';
import {
    Box,
    Button,
    CircularProgress,
    Stack
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';

import { TrackedTextField } from 'src/theme/StyledFields';
import ApiClient from '../../config/ApiClient';
import {
    AssignmentInvitation,
    AssignmentInvitationChannel,
    AssignmentInvitationRole,
} from '../../object-actions/types/types';
import { handleWaiverRequiredPayload } from '../../utils/waiver';

interface InviteUserFormProps {
    assignmentId: number;
    role: AssignmentInvitationRole;
    onInvited?: (invitation: AssignmentInvitation) => void;
    allowSms?: boolean;
    dense?: boolean;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const InviteUserForm: React.FC<InviteUserFormProps> = ({
    assignmentId,
    role,
    onInvited,
    allowSms = false,
    dense = false,
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const [channel, setChannel] = useState<AssignmentInvitationChannel>('email');
    const [name, setName] = useState('');
    const [destination, setDestination] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const placeholder = channel === 'email'
        ? `${roleLabel(role)} email address`
        : `${roleLabel(role)} phone number`;

    const validate = (): string | null => {
        if (!name.trim()) return `Enter the ${roleLabel(role).toLowerCase()}'s full name.`;
        const value = destination.trim();
        if (!value) return `Enter ${channel === 'email' ? 'an email address' : 'a phone number'}.`;
        if (channel === 'email' && !EMAIL_RE.test(value)) return 'Enter a valid email address.';
        if (channel === 'sms' && value.length < 7) return 'Enter a valid phone number.';
        return null;
    };

    const handleSend = async () => {
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }
        setError(null);
        setSending(true);
        try {
            const payload: Record<string, string> = {
                role,
                channel,
                name: name.trim(),
            };
            if (channel === 'email') {
                payload.email = destination.trim().toLowerCase();
            } else {
                payload.phone = destination.trim();
            }
            const res = await ApiClient.post<AssignmentInvitation>(
                `/api/payment-assignments/${assignmentId}/invite/`,
                payload,
            );
            if (res.success && res.data) {
                const wasResend = res.data.is_resend;
                enqueueSnackbar(wasResend ? 'Invitation re-sent' : 'Invitation sent', { variant: 'success' });
                setName('');
                setDestination('');
                if (onInvited) onInvited(res.data);
            } else {
                if (handleWaiverRequiredPayload(res.errors as any, role, assignmentId)) {
                    const message = 'Please sign the waiver for your role before inviting others.';
                    setError(message);
                    enqueueSnackbar(message, { variant: 'warning' });
                } else {
                    const message = formatInviteError(res.error, res.errors);
                    setError(message);
                    enqueueSnackbar(message, { variant: 'error' });
                }
            }
        } catch (e: any) {
            const message = e?.message || 'Failed to send invitation';
            setError(message);
            enqueueSnackbar(message, { variant: 'error' });
        } finally {
            setSending(false);
        }
    };

    return (
        <Stack
            spacing={1}
            sx={{ mt: dense ? 0.5 : 1 }}
            data-attr={`invite-user-form_role-${role}`}
        >
            <TrackedTextField
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={`${roleLabel(role)} full name`}
                required
                size="small"
                fullWidth
                data-attr={`invite-user-form_name-${role}`}
            />
            <Box
                sx={{
                    display: 'flex',
                    gap: 1,
                    flexWrap: 'wrap',
                    alignItems: 'flex-start',
                }}
            >
                <TrackedTextField
                    value={destination}
                    onChange={(e) => {
                        setDestination(e.target.value);
                        if (error) setError(null);
                    }}
                    placeholder={placeholder}
                    size="small"
                    type={channel === 'email' ? 'email' : 'tel'}
                    error={!!error}
                    helperText={error || undefined}
                    fullWidth
                    InputProps={{
                        sx: {
                            '&.MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
                                paddingRight: '4.5px',
                            },
                            '& .MuiInputAdornment-positionEnd': {
                                marginLeft: '8.5px',
                                marginRight: 0,
                            },
                        }
                    }}
                />
                <Button
                    onClick={handleSend}
                    size="small"
                    variant="contained"
                    disabled={sending}
                    startIcon={
                        sending ? <CircularProgress color="inherit" size={14} /> : <SendIcon fontSize="small" />
                    }
                    data-attr={`invite-user-form_send-${role}`}
                >
                    {sending ? 'Sending' : 'Invite'}
                </Button>
            </Box>
        </Stack>
    );
};

function roleLabel(role: AssignmentInvitationRole): string {
    switch (role) {
        case 'athlete':
            return 'Athlete';
        case 'coach':
            return 'Coach';
        case 'parent':
            return 'Guardian';
        default:
            return role;
    }
}

function formatInviteError(err: any, errors?: { [key: string]: any }): string {
    const retryAfter = getRetryAfterSeconds(errors) ?? getRetryAfterSeconds(err);
    if (retryAfter !== null) {
        const minutes = Math.max(1, Math.ceil(retryAfter / 60));
        return `Invitation was sent recently. Try again in about ${minutes} minute${minutes === 1 ? '' : 's'}.`;
    }
    return formatError(err);
}

function getRetryAfterSeconds(payload: unknown): number | null {
    if (payload == null || typeof payload !== 'object') {
        return null;
    }
    const seconds = Number((payload as { retry_after_seconds?: unknown }).retry_after_seconds);
    return Number.isFinite(seconds) && seconds > 0 ? seconds : null;
}

function formatError(err: any): string {
    if (!err) return 'Failed to send invitation';
    if (typeof err === 'string') return err;
    if (typeof err === 'object') {
        const flat = Object.values(err).flat();
        if (flat.length) return flat.join('; ');
    }
    return 'Failed to send invitation';
}

export default InviteUserForm;
